import { useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import StageColumn from './StageColumn.jsx';
import MobilePipelineBoard from './MobilePipelineBoard.jsx';
import MoveToMenu from './MoveToMenu.jsx';
import MoveToConfirmDialog from './MoveToConfirmDialog.jsx';
import ComposeSheet from '@/components/comms/ComposeSheet.jsx';
import useMediaQuery from '@/hooks/useMediaQuery.js';
import { ACTIVE_STAGES, TERMINAL_STAGES } from '@/services/pipeline.service.js';
import { updateDealStage } from '@/services/deals.service.js';
import { tokenForItem, toastNotice, TOKEN_FAILED_WORDS } from '@/services/session-token.service.js';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';

// Kanban board. Renders desktop columns side-by-side; mobile gets a
// dedicated swipe-between-stages layout via MobilePipelineBoard.
//
// Drag-and-drop: desktop only (spec §3.8). Mobile broker uses the
// ⋯ button on each card → Move-to menu (handled inside StageColumn).
// DndContext still wraps the mobile layout because PointerSensor
// activationConstraint:8px means accidental touches never trigger
// drag — keeping the wrapper avoids needing two code paths.

const TERMINAL = new Set(TERMINAL_STAGES);
const STAGE_ORDER_INDEX = new Map(
  [...ACTIVE_STAGES, ...TERMINAL_STAGES].map((s, i) => [s, i])
);

function isBackwardMove(from, to) {
  const ai = STAGE_ORDER_INDEX.get(from);
  const bi = STAGE_ORDER_INDEX.get(to);
  if (ai == null || bi == null) return false;
  return bi < ai && !TERMINAL.has(to);
}

function needsConfirm(from, to) {
  return TERMINAL.has(to) || isBackwardMove(from, to);
}

export default function PipelineBoard({ data, sortBy = 'urgency', showClosed = false, onChange }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [optimistic, setOptimistic] = useState({});
  const [menuState, setMenuState] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [composeState, setComposeState] = useState(null); // { sessionToken, stage }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const stages = showClosed ? [...ACTIVE_STAGES, ...TERMINAL_STAGES] : ACTIVE_STAGES;

  const merged = useMemo(() => {
    if (!data) return null;
    const byStage = {};
    for (const s of stages) byStage[s] = [];
    for (const stage of stages) {
      for (const item of data.byStage[stage] || []) {
        const target = optimistic[item.deal_id] || stage;
        if (byStage[target]) {
          byStage[target].push({ ...item, deal_stage: target });
        }
      }
    }
    return byStage;
  }, [data, optimistic, stages]);

  if (!data || !merged) return null;

  function currentStageFor(dealId) {
    for (const s of stages) {
      if ((merged[s] || []).some((i) => String(i.deal_id) === String(dealId))) {
        return s;
      }
    }
    return null;
  }

  async function fireMove(dealId, newStage, reason) {
    setOptimistic((prev) => ({ ...prev, [dealId]: newStage }));
    try {
      await updateDealStage(dealId, newStage, reason ? { reason } : undefined);
      toast.success(`Moved → ${newStage}`);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[dealId];
        return next;
      });
      const msg =
        err instanceof ApiError ? err.body?.error || err.code : err.message || 'failed';
      toast.error(`Couldn't move deal: ${msg}`);
      throw err;
    }
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;
    const dealId = String(active.id);
    const newStage = String(over.id);
    const fromStage = currentStageFor(dealId);
    if (!fromStage || fromStage === newStage) return;
    // DEAL-LIFECYCLE-SPEC Phase 1: lifecycle state is derived from ground
    // truth. Only the terminal states are manual moves; dragging into any
    // other column would be a display-fixing write, which no longer exists.
    if (!TERMINAL.has(newStage)) {
      toast.info('Stage is derived from deal activity. Only Lost, Cancelled, and Expired are manual moves.');
      return;
    }
    const item = (merged[fromStage] || []).find((i) => String(i.deal_id) === dealId);
    setConfirmState({ item, fromStage, toStage: newStage });
  }

  function handleCardContextMenu(item, position) {
    setMenuState({ item, position });
  }

  function handleMenuSelect(toStage) {
    if (!menuState) return;
    const item = menuState.item;
    setMenuState(null);
    const fromStage = item.deal_stage || currentStageFor(item.deal_id);
    if (needsConfirm(fromStage, toStage)) {
      setConfirmState({ item, fromStage, toStage });
    } else {
      fireMove(String(item.deal_id), toStage, null).catch(() => {});
    }
  }

  async function handleConfirm(reason) {
    if (!confirmState) return;
    await fireMove(String(confirmState.item.deal_id), confirmState.toStage, reason);
  }

  async function handleMenuCompose() {
    if (!menuState) return;
    const item = menuState.item;
    setMenuState(null);
    // The client's session token is fetched for this one deal (the /pipeline list no longer carries
    // every client's token, personal-logins; an older API's item still carries it). A failed fetch is
    // said, not shown as "no session"; a slow one shows a pending notice.
    const t = await tokenForItem({ item, onSlow: toastNotice(toast) });
    if (!t.ok) toast.info(TOKEN_FAILED_WORDS);
    // No session guard: a sessionless deal still opens Compose and sends the
    // deal-keyed broker templates (ComposeSheet falls back to the deal-keyed
    // path on dealId when sessionToken is null). Pre-Stage adds the session
    // for the client-link templates.
    // Stash the FULL context so ComposeSheet derives suggestions from
    // days_until_expiration + days_since_last_activity (Vision §V2 fix
    // #2, 2026-05-20). Static stage-only map removed.
    setComposeState({
      sessionToken: t.token,
      dealId: item.deal_id,
      stage: item.deal_stage,
      dealContext: {
        stage: item.deal_stage,
        daysUntilExpiration: item.days_until_expiration ?? null,
        daysSinceLastActivity: item.days_since_last_activity ?? null,
      },
    });
  }

  // Pass merged-shape down to mobile layout too.
  const mergedAsData = { ...data, byStage: merged };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {isDesktop ? (
          <div className="flex w-full gap-4 overflow-x-auto p-4 md:p-6">
            {stages.map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                items={merged[stage] || []}
                sortBy={sortBy}
                onContextMenu={handleCardContextMenu}
                onChange={onChange}
              />
            ))}
          </div>
        ) : (
          <MobilePipelineBoard
            data={mergedAsData}
            stages={stages}
            sortBy={sortBy}
            onContextMenu={handleCardContextMenu}
            onChange={onChange}
          />
        )}
      </DndContext>
      <MoveToMenu
        open={!!menuState}
        anchorPosition={menuState?.position}
        fromStage={menuState?.item?.deal_stage}
        onSelect={handleMenuSelect}
        onCompose={handleMenuCompose}
        onClose={() => setMenuState(null)}
      />
      <ComposeSheet
        sessionToken={composeState?.sessionToken || null}
        dealId={composeState?.dealId || null}
        open={!!composeState}
        onOpenChange={(o) => !o && setComposeState(null)}
        dealContext={composeState?.dealContext || null}
        onSent={() => {
          setComposeState(null);
          if (typeof onChange === 'function') onChange();
        }}
      />
      <MoveToConfirmDialog
        open={!!confirmState}
        onOpenChange={(o) => !o && setConfirmState(null)}
        fromStage={confirmState?.fromStage}
        toStage={confirmState?.toStage}
        onConfirm={handleConfirm}
      />
    </>
  );
}
